'use client';

import { useMemo, useState } from 'react';
import { Building2Icon, CheckIcon, ChevronsUpDownIcon } from 'lucide-react';
import { toast } from 'sonner';

import { useApiV1Ctx } from '@/components/providers/v1-api';
import { useMeCtx } from '@/components/providers/v1-me-provider';
import { useOfficeCtx } from '@/components/providers/v1-office-provider';
import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { SidebarMenu, SidebarMenuButton, SidebarMenuItem } from '@/components/ui/sidebar';

export function NavOfficeSwitcher() {
  const { office } = useOfficeCtx();
  const offices = useMyOffices();
  const [open, setOpen] = useState(false);

  if (offices.length <= 1) return null;

  return (
    <SidebarMenu>
      <SidebarMenuItem>
        <SidebarMenuButton className='text-foreground' onClick={() => setOpen(true)}>
          <Building2Icon />
          <span className='truncate'>{office.name}</span>
          <ChevronsUpDownIcon className='ml-auto' />
        </SidebarMenuButton>
        <OfficeSwitcherDialog open={open} onOpenChange={setOpen} offices={offices} />
      </SidebarMenuItem>
    </SidebarMenu>
  );
}

interface OfficeSwitcherDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  offices: MyOffice[];
}

function OfficeSwitcherDialog({ open, onOpenChange, offices }: OfficeSwitcherDialogProps) {
  const { api } = useApiV1Ctx();
  const { me } = useMeCtx();
  const [query, setQuery] = useState('');
  const mutation = api.employees.switchToEmployee.useMutation(undefined, {
    onSuccess: () => {
      toast.success('Switched office');
      window.location.reload();
    },
  });

  // Filtered here rather than by cmdk, which re-sorts matches by score and breaks alphabetical order
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return offices;
    return offices.filter((office) => office.officeName.toLowerCase().includes(q));
  }, [offices, query]);

  const onOpenChangeAndReset = (next: boolean) => {
    if (!next) setQuery('');
    onOpenChange(next);
  };

  return (
    <CommandDialog
      open={open}
      onOpenChange={onOpenChangeAndReset}
      shouldFilter={false}
      title='Switch office'
      description='Search for an office to switch to'
    >
      <CommandInput placeholder='Search offices...' value={query} onValueChange={setQuery} />
      <CommandList>
        <CommandEmpty>No offices found.</CommandEmpty>
        <CommandGroup heading='Your offices'>
          {filtered.map((office) => {
            const isCurrent = office.officeId === me?.office_id;
            return (
              <CommandItem
                key={office.officeId}
                value={office.officeId}
                disabled={mutation.isPending}
                onSelect={() => {
                  onOpenChangeAndReset(false);
                  if (isCurrent) return;
                  mutation.mutate({ query: { employee_id: office.employeeId } });
                }}
              >
                <Building2Icon />
                <span className='truncate'>{office.officeName}</span>
                {isCurrent && <CheckIcon className='ml-auto' />}
              </CommandItem>
            );
          })}
        </CommandGroup>
      </CommandList>
    </CommandDialog>
  );
}

/**
 * Offices the current user can switch to, derived from their employee records and sorted by name.
 * Switching office means switching to the user's employee record in that office.
 */
function useMyOffices() {
  const { api } = useApiV1Ctx();
  const { me } = useMeCtx();
  const { data: employees = [] } = api.employees.meOptions.useQuery();

  return useMemo(() => {
    const byOffice = new Map<string, MyOffice>();
    for (const employee of employees) {
      const existing = byOffice.get(employee.office_id);
      // Prefer the current employee record when the user has several in the same office
      if (existing && employee.id !== me?.id) continue;
      byOffice.set(employee.office_id, {
        officeId: employee.office_id,
        officeName: employee.office_name,
        employeeId: employee.id,
      });
    }
    return [...byOffice.values()].sort((a, b) =>
      a.officeName.localeCompare(b.officeName, undefined, { sensitivity: 'base' })
    );
  }, [employees, me?.id]);
}

interface MyOffice {
  officeId: string;
  officeName: string;
  employeeId: string;
}
